import { Link } from "react-router-dom";
import { ArrowRight, Scale, Eye, Heart, Globe, Shield, FileText, Handshake } from "lucide-react";
import { Navigation, Footer, ScriptureBlock } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { ScrollReveal } from "@/hooks/useScrollReveal";
import { CovenantPanel } from "@/components/CovenantPanel";

const HERO_IMAGE = "https://images.unsplash.com/photo-1728843501798-d03c8875cf43?crop=entropy&cs=srgb&fm=jpg&q=85";

const scriptureCards = [
  {
    icon: Scale,
    verse: "A false balance is abomination to the Lord: but a just weight is his delight.",
    reference: "Proverbs 11:1",
    explanation: "No hidden spreads. Clear economics. We tell you exactly how we make money on every deal."
  },
  {
    icon: Eye,
    verse: "Providing for honest things, not only in the sight of the Lord, but also in the sight of men.",
    reference: "2 Corinthians 8:21",
    explanation: "We aim to do what is right before God and people—transparent in all dealings."
  },
  {
    icon: Heart,
    verse: "Thou shalt love thy neighbour as thyself.",
    reference: "Matthew 22:39",
    explanation: "We pursue fairness and respect in every transaction. Your success matters to us."
  },
  {
    icon: Globe,
    verse: "The earth is the Lord's, and the fulness thereof.",
    reference: "Psalm 24:1",
    explanation: "We're stewards, not owners. Every parcel of land belongs to God—we help facilitate His purposes."
  }
];

export default function BuilderHome() {
  return (
    <div className="min-h-screen bg-white" data-testid="builder-home">
      <SEO 
        title="Land for Builders | Transparent Land Opportunities"
        description="Find ethical land opportunities for builders and developers. Transparent pricing, full disclosure, no hidden spreads. Steward Lots serves builders in Ohio and nationwide."
        path="/builder"
      />
      <Navigation mode="builder" />

      {/* Hero Section */}
      <section className="relative pt-16 min-h-[70vh] flex items-center" data-testid="builder-hero">
        <img 
          src={HERO_IMAGE}
          alt="Land development opportunity - open field ready for builders seeking ethical land sourcing"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-[#0F0F10]/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="inline-block px-4 py-1 bg-[#BFA15F]/20 text-[#BFA15F] text-sm font-medium tracking-wide uppercase gentle-reveal">
                Builder / Buyer Mode
              </span>
              <div className="live-indicator text-xs text-[#A8A29E] gentle-reveal" style={{ animationDelay: '0.2s' }}>
                <span className="live-dot" aria-hidden="true"></span>
                <span>Opportunities available</span>
              </div>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6 gentle-reveal" style={{ animationDelay: '0.3s' }}>
              Land Opportunities for Builders.<br />
              <span className="text-[#BFA15F]">Honest Stewardship.</span>
            </h1>
            <p className="text-lg text-[#E7E5E4] mb-8 max-w-lg gentle-reveal" style={{ animationDelay: '0.5s' }}>
              Access curated land opportunities with full disclosure, clear economics, and integrity-driven land facilitation at every step.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 gentle-reveal" style={{ animationDelay: '0.7s' }}>
              <Link to="/builder/request" aria-label="Request a builder information packet">
                <Button 
                  size="lg" 
                  className="btn-enhanced bg-white text-[#0F0F10] hover:bg-[#F5F5F4] font-medium px-8"
                  data-testid="request-packet-btn"
                >
                  Request a Packet
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/builder/buybox" aria-label="Submit your land buying criteria">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10 font-medium px-8"
                  data-testid="submit-buybox-btn"
                >
                  Submit Your Buy Box
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Message */}
      <ScrollReveal animation="reveal-up">
        <section className="py-16 px-6 md:px-12 bg-[#FBF9F3]" data-testid="welcome-section">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block border-l-[3px] border-[#BFA15F] pl-4 text-left mb-6">
              <p className="font-serif italic text-[#57534E]">
                "Except the Lord build the house, they labour in vain that build it."
              </p>
              <p className="text-sm text-[#A8A29E] mt-1 uppercase tracking-wide">Psalm 127:1</p>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-4">
              Welcome, Builder
            </h2>
            <p className="text-[#57534E] max-w-2xl mx-auto">
              You build homes and communities. We want to serve you with land opportunities sourced ethically, 
              priced fairly, and presented with complete transparency. Here's how we're different.
            </p>
          </div>
        </section>
      </ScrollReveal>

      {/* Covenant Transparency Panels */}
      <ScrollReveal animation="reveal-up">
        <section className="py-16 px-6 md:px-12" data-testid="transparency-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4 text-center">
              How We Serve Builders with Integrity
            </h2>
            <p className="text-[#57534E] text-center mb-10 max-w-2xl mx-auto">
              Expand each section to see exactly how our Covenant Accountability Model protects your interests.
            </p>
            
            <div className="space-y-4">
              <CovenantPanel
                title="How Land Is Sourced & Evaluated"
                scripture="A false balance is abomination to the Lord"
                reference="Proverbs 11:1"
                icon={FileText}
              >
                <p>
                  Every land opportunity we present has been evaluated for title clarity, zoning compliance, 
                  utility access, and comparable market values. We document what we know—and what we don't know.
                </p>
                <p>
                  We disclose our acquisition price and our target margin. You see the full picture, 
                  not a number designed to extract maximum profit from you.
                </p>
                <p className="font-medium text-[#1C1917]">
                  Our commitment: No hidden spreads. No manufactured urgency. Just honest numbers.
                </p>
              </CovenantPanel>

              <CovenantPanel
                title="How Builders Are Protected"
                scripture="Providing for honest things, not only in the sight of the Lord"
                reference="2 Corinthians 8:21"
                icon={Shield}
              >
                <p>
                  When you receive a builder packet from Steward Lots™, you receive full disclosure of all 
                  known facts, issues, and considerations. We don't hide problems to close deals.
                </p>
                <p>
                  Assignment fees, transaction structures, and timeline expectations are documented in writing 
                  before you commit. If something changes, we communicate immediately.
                </p>
                <p className="font-medium text-[#1C1917]">
                  Our commitment: You will never be surprised by hidden terms or undisclosed conditions.
                </p>
              </CovenantPanel>

              <CovenantPanel
                title="How Ethical Facilitation Works"
                scripture="Thou shalt love thy neighbour as thyself"
                reference="Matthew 22:39"
                icon={Handshake}
              >
                <p>
                  We facilitate connections between motivated sellers and qualified builders. We're transparent 
                  about our role—we're not pretending to be something we're not.
                </p>
                <p>
                  Fair pricing is grounded in real builder math. We consider your costs, margins, and risk 
                  when structuring deals. A deal that doesn't work for you doesn't work for us.
                </p>
                <p className="font-medium text-[#1C1917]">
                  Our commitment: We would rather lose a deal than violate God's commands or damage a relationship.
                </p>
              </CovenantPanel>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* How We Operate Section */}
      <section className="py-24 px-6 md:px-12 bg-[#F5F5F4]" data-testid="scripture-section">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal animation="reveal-up">
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">
                Scripture-Guided Land Facilitation
              </h2>
              <p className="text-[#57534E] text-lg">
                Our business practices are rooted in Scripture—here's why that matters for land buyers and builders.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {scriptureCards.map((card, index) => (
              <ScrollReveal key={index} animation="reveal-up" delay={index + 1}>
                <article 
                  className="scripture-card interactive-card bg-white p-8 border border-[#E7E5E4]"
                  data-testid={`scripture-card-${index}`}
                >
                  <card.icon className="w-8 h-8 text-[#BFA15F] mb-4" aria-hidden="true" />
                  <ScriptureBlock 
                    verse={card.verse}
                    reference={card.reference}
                    className="mb-4"
                  />
                  <p className="text-[#57534E]">{card.explanation}</p>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* What You Receive */}
      <ScrollReveal animation="reveal-up">
        <section className="py-24 px-6 md:px-12" data-testid="what-you-receive">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6">
                  What Land Buyers and Builders Receive
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4 group">
                    <div className="w-10 h-10 bg-[#0F0F10] text-white flex items-center justify-center font-medium flex-shrink-0 group-hover:bg-[#BFA15F] transition-colors" aria-hidden="true">
                      1
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1C1917] mb-1">Clean Builder Packets</h3>
                      <p className="text-[#57534E]">Professionally prepared documentation with all known facts, title information, zoning details, and property specifications for informed decision-making.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 group">
                    <div className="w-10 h-10 bg-[#0F0F10] text-white flex items-center justify-center font-medium flex-shrink-0 group-hover:bg-[#BFA15F] transition-colors" aria-hidden="true">
                      2
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1C1917] mb-1">Full Disclosure of Known Facts</h3>
                      <p className="text-[#57534E]">We disclose everything we know about every property—no surprises, no hidden issues, no games. This is the foundation of ethical land wholesaling.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 group">
                    <div className="w-10 h-10 bg-[#0F0F10] text-white flex items-center justify-center font-medium flex-shrink-0 group-hover:bg-[#BFA15F] transition-colors" aria-hidden="true">
                      3
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1C1917] mb-1">Straightforward Offer Process</h3>
                      <p className="text-[#57534E]">Clear timelines, fair terms, and honest communication throughout. We respect your time and capital.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-[#F5F5F4] p-8 border border-[#E7E5E4] interactive-card">
                <ScriptureBlock
                  verse="We would rather lose a deal than violate God's commands."
                  reference="Our Commitment"
                />
                <p className="mt-6 text-[#57534E]">
                  This isn't marketing language—it's how we actually operate. Every deal, every time. 
                  Learn more about <Link to="/faith" className="text-[#BFA15F] hover:underline">our faith and operating principles</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-[#0F0F10]" data-testid="builder-cta">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollReveal animation="reveal-up">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
              Ready to Work with Steward Lots?
            </h2>
            <p className="text-[#A8A29E] text-lg mb-8 max-w-2xl mx-auto">
              Submit your buy box criteria or request a builder packet. We respond with clarity, transparency, and clear next steps. 
              If you're a <Link to="/seller" className="text-[#BFA15F] hover:underline">land seller</Link>, visit our seller page instead.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/builder/buybox" aria-label="Submit your land buying criteria">
                <Button 
                  size="lg" 
                  className="btn-enhanced bg-white text-[#0F0F10] hover:bg-[#F5F5F4] font-medium px-8"
                  data-testid="cta-buybox-btn"
                >
                  Submit Your Buy Box
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/builder/request" aria-label="Request a builder information packet">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10 font-medium px-8"
                  data-testid="cta-request-btn"
                >
                  Request a Packet
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
